import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, Crown, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CoinShopPage() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState({ coin_packages: [], elite: null });
  const [busy, setBusy] = useState("");

  useEffect(() => { (async () => {
    try { const r = await api.get("/payments/catalog"); setCatalog(r.data); } catch { /* ignore */ }
  })(); }, []);

  const buy = async (lookup_key) => {
    if (!user) { toast.error("Please sign in"); return; }
    setBusy(lookup_key);
    try {
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  useEffect(() => { refresh(); }, []);  // eslint-disable-line

  return (
    <div className="min-h-screen pb-28 pt-6 px-4 max-w-md mx-auto">
      <h1 className="font-heading text-3xl font-bold">Coin Shop & Elite</h1>
      <p className="text-slate-400 text-sm">Fuel more scans. Unlock unlimited Jixxeo AI.</p>

      <div className="mt-4 jx-glass rounded-3xl p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-amber-400/20 flex items-center justify-center"><Coins className="text-amber-300" /></div>
        <div className="flex-1"><p className="text-xs text-slate-400 font-mono-data uppercase">Your balance</p><p className="text-2xl font-heading font-black">{user?.coins ?? 0} <span className="text-sm text-slate-500">coins</span></p></div>
        {user?.elite && <span className="px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-300 text-xs inline-flex items-center gap-1"><Crown size={12} /> Elite</span>}
      </div>

      {/* Elite */}
      {catalog.elite && (
        <div className="mt-5 jx-glass rounded-3xl p-5 border border-amber-400/30 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-mono-data uppercase tracking-widest text-amber-300">Jixxeo One Tap Elite</p>
                <h2 className="font-heading text-2xl font-black">${catalog.elite.price} <span className="text-sm text-slate-400">/{catalog.elite.interval}</span></h2>
              </div>
              <Crown size={30} className="text-amber-300" />
            </div>
            <ul className="mt-3 space-y-1.5">
              {catalog.elite.features.map((f) => (
                <li key={f} className="text-sm text-slate-200 flex items-start gap-2"><Check size={14} className="text-emerald-400 mt-0.5" />{f}</li>
              ))}
            </ul>
            <button
              data-testid={`buy-${catalog.elite.lookup_key}`}
              onClick={() => buy(catalog.elite.lookup_key)}
              disabled={busy === catalog.elite.lookup_key || user?.elite}
              className="mt-4 w-full h-12 rounded-2xl bg-amber-400 text-slate-950 font-heading font-bold active:scale-95 disabled:opacity-60"
            >
              {user?.elite ? "You are Elite" : (busy === catalog.elite.lookup_key ? "…" : "Upgrade to Elite")}
            </button>
          </div>
        </div>
      )}

      <h3 className="font-heading font-bold mt-6 mb-2">Coin packages</h3>
      <div className="grid grid-cols-2 gap-2">
        {catalog.coin_packages.map((p) => (
          <div key={p.id} data-testid={`coin-pkg-${p.id}`} className={`jx-glass rounded-2xl p-3 relative ${p.popular ? "border-cyan-400/50" : ""}`}>
            {p.badge && <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full text-[10px] font-mono-data uppercase bg-cyan-400 text-slate-950">{p.badge}</span>}
            <div className="flex items-center gap-2"><Coins size={18} className="text-amber-300" /><p className="font-heading font-bold text-lg">{p.coins}</p></div>
            <p className="text-[11px] text-emerald-300 font-mono-data uppercase">+{p.bonus} bonus</p>
            <p className="text-sm text-slate-400 mt-1">{p.title}</p>
            <button
              data-testid={`buy-${p.lookup_key}`}
              onClick={() => buy(p.lookup_key)}
              disabled={busy === p.lookup_key}
              className="mt-3 w-full h-10 rounded-xl bg-cyan-400 text-slate-950 font-heading font-semibold text-sm active:scale-95 disabled:opacity-60"
            >{busy === p.lookup_key ? "…" : `$${p.price}`}</button>
          </div>
        ))}
      </div>

      <div className="mt-5 jx-glass rounded-2xl p-3">
        <p className="text-[10px] font-mono-data uppercase text-slate-500">Test card</p>
        <p className="text-xs text-slate-300 font-mono-data">4242 4242 4242 4242 · any future date · any CVC</p>
      </div>
    </div>
  );
}
